import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {FlexRow} from '@forecast-it/design-system';
import type {StatusStep} from './useAIChatStream';
import {D} from './design-tokens';
import {CheckDot, SpinnerDot, IdleDot} from './icons';

// ── Tool label translation keys ───────────────────────────────────────────────

const TOOL_KEY_MAP: Record<string, string> = {
	list_projects: 'forecast_agent.step.tool.list_projects',
	get_project_status: 'forecast_agent.step.tool.get_project_status',
	get_portfolio_health: 'forecast_agent.step.tool.get_portfolio_health',
	get_portfolio_summary: 'forecast_agent.step.tool.get_portfolio_summary',
	get_team_utilisation: 'forecast_agent.step.tool.get_team_utilisation',
	get_resource_availability: 'forecast_agent.step.tool.get_resource_availability',
	get_time_entries: 'forecast_agent.step.tool.get_time_entries',
	get_current_datetime: 'forecast_agent.step.tool.get_current_datetime',
	search_projects: 'forecast_agent.step.tool.search_projects',
	get_allocations: 'forecast_agent.step.tool.get_allocations',
	get_person_utilisation: 'forecast_agent.step.tool.get_person_utilisation',
	get_team_availability: 'forecast_agent.step.tool.get_team_availability',
	get_non_billable_time: 'forecast_agent.step.tool.get_non_billable_time',
	get_time_analysis: 'forecast_agent.step.tool.get_time_analysis',
	compare_time_periods: 'forecast_agent.step.tool.compare_time_periods',
	get_project_billing_breakdown: 'forecast_agent.step.tool.get_project_billing_breakdown',
};

// ── Styled components ─────────────────────────────────────────────────────────

const StepsWrapper = styled.div`
	padding-left: 34px;
	margin-top: 6px;
`;

const StepsRow = styled(FlexRow)`
	flex-wrap: wrap;
	gap: 5px;
`;

const StepBadge = styled.span<{state: 'done' | 'active' | 'idle'}>`
	display: inline-flex;
	align-items: center;
	gap: 5px;
	padding: 3px 9px 3px 7px;
	border-radius: 9999px;
	font-size: 11px;
	font-weight: 500;
	letter-spacing: 0.1px;
	animation: ai-fade 0.2s ease-out;
	background: ${({state}) => (state === 'done' ? D.stepDoneBg : state === 'active' ? D.stepActiveBg : D.stepIdleBg)};
	color: ${({state}) => (state === 'done' ? D.stepDoneText : state === 'active' ? D.stepActiveText : D.stepIdleText)};
	border: 1px solid
		${({state}) => (state === 'done' ? D.stepDoneBorder : state === 'active' ? D.stepActiveBorder : D.stepIdleBorder)};
`;

// ── Component ─────────────────────────────────────────────────────────────────

type StatusStepsProps = {
	steps: StatusStep[];
	isActive: boolean;
};

export function StatusSteps({steps, isActive}: StatusStepsProps): JSX.Element {
	const {formatMessage} = useIntl();
	const firstPending = steps.findIndex(s => !s.done);

	function resolveLabel(step: StatusStep): string {
		if (step.stepType === 'thinking') return formatMessage({id: 'forecast_agent.step.thinking'});
		if (step.stepType === 'collating') return formatMessage({id: 'forecast_agent.step.collating'});
		const toolName = step.toolName ?? '';
		const key = TOOL_KEY_MAP[toolName];
		if (key) return formatMessage({id: key});
		return formatMessage({id: 'forecast_agent.step.tool.unknown'}, {toolName});
	}

	return (
		<StepsWrapper>
			<StepsRow>
				{steps.map((step, i) => {
					const isCurrent = isActive && i === firstPending;
					const state = step.done ? 'done' : isCurrent ? 'active' : 'idle';
					return (
						<StepBadge key={step.id} state={state}>
							{step.done ? <CheckDot /> : isCurrent ? <SpinnerDot /> : <IdleDot />}
							{resolveLabel(step)}
						</StepBadge>
					);
				})}
			</StepsRow>
		</StepsWrapper>
	);
}
